import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  deleteAccount,
  enqueueWarmTasks,
  fetchAccount,
  listWarmRuns,
  putAccount,
  type RelayConfig,
} from '../api/client';
import type { AccountRecord, WarmRun } from '../api/types';
import { Avatar } from '../components/Avatar';
import { IconBolt, IconChevronLeft } from '../components/Icon';
import { StatusPill } from '../components/StatusPill';

interface Props {
  cfg: RelayConfig;
}

export function AccountDetail({ cfg }: Props) {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<AccountRecord | null>(null);
  const [runs, setRuns] = useState<WarmRun[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [targetsDraft, setTargetsDraft] = useState('');
  const [savingTargets, setSavingTargets] = useState(false);

  useEffect(() => {
    if (!username) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.url, cfg.token, username]);

  async function refresh() {
    if (!username) return;
    setErr(null);
    try {
      const [r, rr] = await Promise.all([
        fetchAccount(cfg, username),
        listWarmRuns(cfg, { username, limit: 30 }),
      ]);
      setRecord(r);
      setRuns(rr.runs);
      if (r) {
        const { password: _pw, ...editable } = r;
        setDraft(JSON.stringify(editable, null, 2));
        setTargetsDraft((r.target_subreddits ?? []).join(', '));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function save() {
    if (!username) return;
    let parsed: Partial<AccountRecord>;
    try {
      parsed = JSON.parse(draft) as Partial<AccountRecord>;
    } catch (e) {
      setErr(`Invalid JSON: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    setBusy(true);
    try {
      if (record?.password && !('password' in parsed)) {
        parsed.password = record.password;
      }
      await putAccount(cfg, username, parsed);
      setToast('Saved');
      setEditing(false);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function warmNow() {
    if (!username) return;
    setBusy(true);
    try {
      const r = await enqueueWarmTasks(cfg, [username]);
      if (r.failed.length > 0) {
        setErr(r.failed.join('\n'));
      } else {
        setToast('Queued — consumer picks up within ~5 min');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function saveTargets() {
    if (!username || !record) return;
    setSavingTargets(true);
    setErr(null);
    try {
      const targets = parseSubredditList(targetsDraft);
      const merged: Partial<AccountRecord> = {
        ...record,
        target_subreddits: targets,
      };
      // Don't drop password — putAccount overwrites the whole record.
      if (record.password && !('password' in merged)) {
        merged.password = record.password;
      }
      await putAccount(cfg, username, merged);
      setToast(`Saved ${targets.length} target subreddit(s)`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingTargets(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function deleteNow() {
    if (!username) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteAccount(cfg, username);
      // Navigate back to the roster — the account is gone, so nothing to refresh.
      navigate('/');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!username) {
    return <div className="text-[var(--color-text-dim)]">No username in URL.</div>;
  }
  if (!record) {
    return (
      <div className="text-[var(--color-text-dim)]">
        {err ? <span className="text-rose-300">{err}</span> : 'Loading…'}
      </div>
    );
  }

  const w = record.warming;
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-xs text-[var(--color-text-dim)] hover:text-zinc-200"
      >
        <IconChevronLeft width={14} height={14} />
        All accounts
      </Link>

      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Avatar name={record.username} size="md" />
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              {record.username}
            </h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-[var(--color-text-dim)]">
              <span>{record.email ?? '—'}</span>
              <span>·</span>
              <StatusPill status={record.status} />
              {w?.last_status && (
                <>
                  <span className="text-[var(--color-text-faint)]">warming</span>
                  <StatusPill status={w.last_status} />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={warmNow} disabled={busy} className="btn-primary">
            <IconBolt width={14} height={14} />
            Warm now
          </button>
          <button
            onClick={() => setEditing((e) => !e)}
            className="btn-secondary"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button
            onClick={() => {
              setConfirmDelete(true);
              setDeleteTyped('');
            }}
            disabled={busy}
            className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-sm text-rose-200 transition hover:border-rose-500/70 hover:bg-rose-500/20 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </header>

      {confirmDelete && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-4">
          <div className="mb-2 text-sm text-rose-100">
            This deletes <strong>u/{record.username}</strong> from the Worker
            KV and frees its D1 proxy slot. There is no undo.
          </div>
          <div className="mb-3 text-xs text-rose-200/80">
            Type the username to confirm:
          </div>
          <div className="flex gap-2">
            <input
              autoFocus
              value={deleteTyped}
              onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder={record.username}
              className="flex-1 rounded-md border border-rose-500/40 bg-rose-950/40 px-2 py-1 text-sm text-rose-50 placeholder:text-rose-400/60 focus:border-rose-400 focus:outline-none"
            />
            <button
              onClick={deleteNow}
              disabled={busy || deleteTyped !== record.username}
              className="rounded-md border border-rose-500 bg-rose-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Delete account
            </button>
            <button
              onClick={() => {
                setConfirmDelete(false);
                setDeleteTyped('');
              }}
              disabled={busy}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* mini stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MiniStat label="Sessions" value={w?.sessions_count ?? 0} />
        <MiniStat label="Upvotes" value={w?.upvotes_total ?? 0} />
        <MiniStat
          label="Last finished"
          value={w?.last_finished_at ? formatRelative(w.last_finished_at) : 'never'}
        />
        <MiniStat label="Runs" value={runs?.length ?? '—'} />
      </div>

      {toast && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {toast}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
            Target subreddits
          </span>
          <span className="text-xs text-[var(--color-text-faint)]">
            {(record.joined_subreddits ?? []).length} /{' '}
            {(record.target_subreddits ?? []).length} joined
          </span>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-xs text-[var(--color-text-dim)]">
            Comma-separated list. Once non-empty, warming skips{' '}
            <code>/r/popular</code> and instead joins these (max 2/day) then
            browses & upvotes inside them. Leave empty for legacy behavior.
          </p>
          <div className="flex gap-2">
            <input
              value={targetsDraft}
              onChange={(e) => setTargetsDraft(e.target.value)}
              placeholder="python, hiking, machinelearning"
              className="flex-1 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-bg)] px-2 py-1.5 font-mono text-xs text-zinc-200 placeholder:text-[var(--color-text-faint)] focus:border-zinc-600 focus:outline-none"
            />
            <button
              onClick={saveTargets}
              disabled={savingTargets}
              className="btn-primary"
            >
              Save
            </button>
          </div>
          {(record.target_subreddits ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 text-xs">
              {(record.target_subreddits ?? []).map((sub) => {
                const joined = (record.joined_subreddits ?? []).includes(sub);
                return (
                  <span
                    key={sub}
                    className={
                      joined
                        ? 'rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-emerald-200'
                        : 'rounded-md border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-dim)]'
                    }
                    title={joined ? 'joined' : 'not yet joined'}
                  >
                    r/{sub}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
            Record
          </span>
        </div>
        {editing ? (
          <div className="p-4">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={20}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-bg)] p-3 font-mono text-xs text-zinc-200 focus:border-zinc-600 focus:outline-none"
            />
            <div className="mt-3 flex justify-end">
              <button onClick={save} disabled={busy} className="btn-primary">
                Save
              </button>
            </div>
          </div>
        ) : (
          <pre className="overflow-x-auto p-4 font-mono text-xs leading-relaxed text-zinc-300">
            {redactedRecord(record)}
          </pre>
        )}
      </section>

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
            Recent runs
          </span>
          <span className="text-xs tabular-nums text-[var(--color-text-faint)]">
            {runs?.length ?? 0}
          </span>
        </div>
        {!runs && (
          <div className="p-6 text-center text-[var(--color-text-dim)]">Loading…</div>
        )}
        {runs && runs.length === 0 && (
          <div className="p-8 text-center text-sm text-[var(--color-text-dim)]">
            No runs recorded yet.
          </div>
        )}
        {runs && runs.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
              <tr className="border-b border-[var(--color-border)]">
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Activities</th>
                <th className="px-4 py-2 font-medium">Upvotes</th>
                <th className="px-4 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {runs.map((r) => {
                const ok = r.activities.filter((a) => a.ok).length;
                return (
                  <tr key={r.started_at} className="hover:bg-[var(--color-surface-2)]/50">
                    <td className="px-4 py-2 font-mono text-xs text-zinc-400">
                      {r.started_at}
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-4 py-2 tabular-nums text-zinc-400">
                      {ok} / {r.activities.length}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-zinc-400">
                      {r.upvotes_cast}
                    </td>
                    <td className="max-w-md truncate px-4 py-2 text-xs text-[var(--color-text-faint)]">
                      {r.error ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold tabular-nums text-zinc-100">
        {value}
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const dms = Date.now() - t;
  const m = Math.round(dms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function redactedRecord(record: AccountRecord): string {
  const { password: _pw, ...rest } = record;
  return JSON.stringify({ ...rest, password: '<redacted>' }, null, 2);
}

/** Parse a comma/whitespace-separated input into a clean subreddit list.
 * Strips `r/` and `/r/` prefixes, lowercases, drops empties + duplicates. */
function parseSubredditList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[,\s]+/)) {
    const s = piece.trim().replace(/^\/?r\//i, '').toLowerCase();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}
